import React, { useState, useEffect } from 'react';
import { Button, RichTextEditor } from '@/components/ui';
import Input from '@/components/ui/Input/Input';
import { WebsitePageSection } from '@/types';
import { showToast } from '@/lib/toast';
import { ContentWithoutFileFormDataSchema, ContentWithoutFileFormData } from '@/components/Modals/EditContentsModals/EditContentsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

interface InlineTextEditorProps {
  data: WebsitePageSection;
  onSave: (updatedData: Partial<WebsitePageSection>) => void;
  onCancel: () => void;
}

const InlineTextEditor: React.FC<InlineTextEditorProps> = ({ data, onSave, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ContentWithoutFileFormData>({
    resolver: zodResolver(ContentWithoutFileFormDataSchema),
    defaultValues: {
      title: data.title ?? '',
      description: data.description ?? ''
    },
    mode: 'all'
  });

  useEffect(() => {
    reset({
      title: data.title ?? '',
      description: data.description ?? ''
    });
  }, [data, reset]);

  const onSubmit = async (formData: ContentWithoutFileFormData) => {
    try {
      setIsLoading(true);
      onSave({
        title: formData.title ?? '',
        description: formData.description ?? '',
        contentType: 'WITHOUT_FILE'
      });
    } catch (error) {
      showToast.error('An unexpected error occurred.');
      console.error('Update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 bg-white">
      <div className="flex flex-col gap-2">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Title"
              label="Title"
              error={errors.title?.message}
              $dataId="inline-title-input"
              $bgColor="var(--inputBgColor)"
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Page Content</label>
              <RichTextEditor
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder="Description"
                disabled={isLoading}
                  extendedToolbar={{
                            textStyle: false,
                            fontSize: false,
                            image: false,
                          }} 
                toolbarContainerClassName="bg-[var(--inputBgColor)]"
                contentAreaClassName="bg-[var(--inputBgColor)] min-h-[10rem] text-text-primary whitespace-pre-wrap"
              />
              {errors.description?.message && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>
          )}
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default InlineTextEditor;

